import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import { MarketingCampaignEntryStatus } from '@principle-theorem/principle-core/interfaces';
import {
  MarketingStatusColour,
  MarketingStatusIcon,
} from '../../campaign-status/marketing-status-display';
import { IconChipComponent } from '../../icon-chip/icon-chip.component';

const statusColourMap: { [key in MarketingCampaignEntryStatus]: string } = {
  [MarketingCampaignEntryStatus.Preview]: MarketingStatusColour.Draft,
  [MarketingCampaignEntryStatus.Approved]: MarketingStatusColour.Draft,
  [MarketingCampaignEntryStatus.Pending]: MarketingStatusColour.Draft,
  [MarketingCampaignEntryStatus.Active]: MarketingStatusColour.Success,
  [MarketingCampaignEntryStatus.Completed]: MarketingStatusColour.Success,
  [MarketingCampaignEntryStatus.Converted]: MarketingStatusColour.Success,
  [MarketingCampaignEntryStatus.Failed]: MarketingStatusColour.Error,
  [MarketingCampaignEntryStatus.Unsubscribed]: MarketingStatusColour.Warning,
  [MarketingCampaignEntryStatus.Unenrolled]: MarketingStatusColour.Warning,
  [MarketingCampaignEntryStatus.Paused]: MarketingStatusColour.Warning,
  [MarketingCampaignEntryStatus.PausedForPayment]: MarketingStatusColour.Warning,
};

const statusIconMap: { [key in MarketingCampaignEntryStatus]: string } = {
  [MarketingCampaignEntryStatus.Preview]: MarketingStatusIcon.Pending,
  [MarketingCampaignEntryStatus.Approved]: MarketingStatusIcon.Pending,
  [MarketingCampaignEntryStatus.Pending]: MarketingStatusIcon.Pending,
  [MarketingCampaignEntryStatus.Active]: MarketingStatusIcon.Running,
  [MarketingCampaignEntryStatus.Completed]: MarketingStatusIcon.Complete,
  [MarketingCampaignEntryStatus.Converted]: MarketingStatusIcon.Complete,
  [MarketingCampaignEntryStatus.Failed]: MarketingStatusIcon.Failed,
  [MarketingCampaignEntryStatus.Unsubscribed]: MarketingStatusIcon.Exit,
  [MarketingCampaignEntryStatus.Unenrolled]: MarketingStatusIcon.Exit,
  [MarketingCampaignEntryStatus.Paused]: MarketingStatusIcon.Paused,
  [MarketingCampaignEntryStatus.PausedForPayment]: MarketingStatusIcon.Payment,
};

const statusLabelMap: { [key in MarketingCampaignEntryStatus]: string } = {
  [MarketingCampaignEntryStatus.Preview]: 'Preview',
  [MarketingCampaignEntryStatus.Approved]: 'Approved',
  [MarketingCampaignEntryStatus.Pending]: 'Pending',
  [MarketingCampaignEntryStatus.Active]: 'Active',
  [MarketingCampaignEntryStatus.Completed]: 'Completed',
  [MarketingCampaignEntryStatus.Converted]: 'Converted',
  [MarketingCampaignEntryStatus.Failed]: 'Failed',
  [MarketingCampaignEntryStatus.Unsubscribed]: 'Unsubscribed',
  [MarketingCampaignEntryStatus.Unenrolled]: 'Unenrolled',
  [MarketingCampaignEntryStatus.Paused]: 'Paused',
  [MarketingCampaignEntryStatus.PausedForPayment]: 'Paused (Payment)',
};

@Component({
  selector: 'pr-campaign-entry-status',
  templateUrl: './campaign-entry-status.component.html',
  styleUrl: './campaign-entry-status.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgCommonModule, IconChipComponent, MatTooltipModule],
})
export class CampaignEntryStatusComponent {
  status = input<MarketingCampaignEntryStatus>();
  errorMessage = input<string>();
  statusColour = computed(() => this._getStatusColour(this.status()));
  statusIcon = computed(() => this._getStatusIcon(this.status()));
  statusLabel = computed(() => this._getStatusLabel(this.status()));

  private _getStatusColour(status?: MarketingCampaignEntryStatus): string {
    return status ? statusColourMap[status] : MarketingStatusColour.Draft;
  }

  private _getStatusIcon(status?: MarketingCampaignEntryStatus): string {
    return status ? statusIconMap[status] : MarketingStatusIcon.Unknown;
  }

  private _getStatusLabel(status?: MarketingCampaignEntryStatus): string {
    return status ? statusLabelMap[status] : '';
  }
}
